import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, Sparkle, X, Plus, PencilSimple } from '@phosphor-icons/react';

const DAYS = [
  { id: 'mon', label: 'Monday' },
  { id: 'tue', label: 'Tuesday' },
  { id: 'wed', label: 'Wednesday' },
  { id: 'thu', label: 'Thursday' },
  { id: 'fri', label: 'Friday' },
];
const BOXES = [
  { id: 'school_tiffin', label: 'School Tiffin', emoji: '🍛' },
  { id: 'school_snack', label: 'School Snack', emoji: '🍎' },
  { id: 'bus_snack', label: 'Bus Snack', emoji: '🚌' },
];

const addDays = (iso, n) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmt = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

function RotationEditor({ api, kid, onSaved }) {
  const [items, setItems] = useState(kid.dabba_rotation || []);
  const [input, setInput] = useState('');
  const [dirty, setDirty] = useState(false);

  const addFromText = (text) => {
    const parts = text.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setItems(prev => [...new Set([...prev, ...parts])].slice(0, 40));
    setDirty(true);
    setInput('');
  };

  const save = async () => {
    try {
      await api.put(`/dabba/rotation/${kid.kid_id}`, { items });
      toast.success(`${kid.name}'s food list saved`);
      setDirty(false);
      onSaved();
    } catch (e) { toast.error('Could not save list'); }
  };

  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }} data-testid={`dabba-rotation-${kid.kid_id}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2">
        Current rotation — foods {kid.name} already eats (AI keeps ~70% from here)
      </p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-200 text-xs">
            {it}
            <button onClick={() => { setItems(items.filter((_, j) => j !== i)); setDirty(true); }} data-testid={`dabba-rotation-remove-${kid.kid_id}-${i}`}
              className="text-amber-300/60 hover:text-red-300"><X size={11} /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-neutral-500 italic">No foods yet — add or paste a list below</span>}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFromText(input); } }}
          onPaste={e => { const t = e.clipboardData.getData('text'); if (/[\n,;]/.test(t)) { e.preventDefault(); addFromText(t); } }}
          placeholder="e.g. Aloo paratha, Poha, Idli… (paste a comma/line list)"
          data-testid={`dabba-rotation-input-${kid.kid_id}`}
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50" />
        <button onClick={() => addFromText(input)} className="px-2.5 rounded-lg bg-white/5 border border-white/10 text-neutral-300 hover:text-white"><Plus size={13} /></button>
        {dirty && (
          <button onClick={save} data-testid={`dabba-rotation-save-${kid.kid_id}`}
            className="px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-200 text-xs font-bold">Save</button>
        )}
      </div>
    </div>
  );
}

function BoxCell({ plan, day, box, api, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const value = plan?.days?.[day]?.[box.id] || '';
  const [draft, setDraft] = useState(value);
  const isNew = /\(new\)\s*$/i.test(value);
  const display = value.replace(/\s*\(new\)\s*$/i, '');

  const save = async () => {
    setEditing(false);
    if (draft.trim() === value) return;
    try {
      await api.put(`/dabba/plans/${plan.plan_id}`, { day, box: box.id, value: draft.trim() });
      onUpdate();
    } catch (e) { toast.error('Could not save'); }
  };

  if (editing) {
    return (
      <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        data-testid={`dabba-cell-input-${plan.kid_id}-${day}-${box.id}`}
        className="w-full bg-white/10 border border-amber-500/50 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none" />
    );
  }
  return (
    <button onClick={() => { setDraft(value); setEditing(true); }} data-testid={`dabba-cell-${plan.kid_id}-${day}-${box.id}`}
      className="w-full text-left px-2 py-1.5 rounded-lg text-xs group hover:bg-white/5 transition-colors min-h-[30px]">
      {display ? (
        <span className="text-neutral-200">
          {display}
          {isNew && <span className="ml-1.5 text-[9px] font-bold text-purple-300 bg-purple-500/15 border border-purple-500/30 rounded-full px-1.5 py-px">✨ new</span>}
        </span>
      ) : (
        <span className="text-neutral-600 italic flex items-center gap-1">— <PencilSimple size={10} className="opacity-0 group-hover:opacity-100" /></span>
      )}
    </button>
  );
}

export default function DabbaTab({ api }) {
  const [data, setData] = useState(null);
  const [weekStart, setWeekStart] = useState('');
  const [generating, setGenerating] = useState('');
  const [loading, setLoading] = useState(true);
  const [align, setAlign] = useState(true);
  const [jainFlags, setJainFlags] = useState({});

  useEffect(() => {
    if (!weekStart) return;
    const dates = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
    api.get(`/jain/flags?dates=${dates.join(',')}`).then(r => setJainFlags(r.data || {})).catch(() => {});
  }, [api, weekStart]);

  const load = useCallback(async (start = '') => {
    try {
      const r = await api.get(`/dabba/week${start ? `?start=${start}` : ''}`);
      setData(r.data);
      setWeekStart(r.data.week_start);
      setAlign(r.data.settings?.align_with_menu ?? true);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  const toggleAlign = async () => {
    const next = !align;
    setAlign(next);
    try {
      await api.put('/dabba/settings', { align_with_menu: next });
      toast.success(next
        ? 'Aligned: tiffin + family menu will share morning prep 🍳'
        : 'Independent: tiffin planned separately from the family menu');
    } catch (e) { setAlign(!next); toast.error('Could not save setting'); }
  };

  useEffect(() => { load(); }, [load]);

  const generate = async (kidId = '') => {
    setGenerating(kidId || 'all');
    try {
      await api.post('/dabba/generate', { week_start: weekStart, kid_id: kidId });
      toast.success(kidId ? 'Dabba plan ready! 🍱' : 'Dabba plans ready for all kids! 🍱');
      await load(weekStart);
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    setGenerating('');
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  const kids = data?.kids || [];
  const plans = data?.plans || {};

  return (
    <div className="space-y-5" data-testid="dabba-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-white font-bold text-lg flex items-center gap-2">🍱 Kids Dabba Planner</h2>
          <p className="text-neutral-500 text-xs mt-0.5">School tiffin, school snack & bus snack — Mon to Fri, per kid. AI keeps ~70% familiar, ~30% new.</p>
          <button onClick={toggleAlign} data-testid="dabba-align-toggle"
            className={`mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${align
              ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40'
              : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}
            title="When ON: dabba generation sees the week's family breakfast/lunch, and the AI Chef sees the tiffin plan — so the cook can batch morning prep.">
            <span className={`w-7 h-4 rounded-full relative transition-colors ${align ? 'bg-emerald-500' : 'bg-neutral-600'}`}>
              <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${align ? 'left-3.5' : 'left-0.5'}`} />
            </span>
            Align with family menu {align ? 'ON' : 'OFF'}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(addDays(weekStart, -7))} data-testid="dabba-week-prev"
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-neutral-300 hover:text-white"><CaretLeft size={14} /></button>
          <span className="text-xs font-semibold text-neutral-300 min-w-[120px] text-center" data-testid="dabba-week-label">
            {fmt(weekStart)} – {fmt(addDays(weekStart, 4))}
          </span>
          <button onClick={() => load(addDays(weekStart, 7))} data-testid="dabba-week-next"
            className="p-2 rounded-lg bg-white/5 border border-white/10 text-neutral-300 hover:text-white"><CaretRight size={14} /></button>
          {kids.length > 1 && (
            <button onClick={() => generate('')} disabled={!!generating} data-testid="dabba-generate-all-btn"
              className="ml-2 px-3 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
              <Sparkle size={13} weight="fill" className={generating === 'all' ? 'animate-spin' : ''} />
              {generating === 'all' ? 'Planning…' : 'Generate All'}
            </button>
          )}
        </div>
      </div>

      {kids.length === 0 && (
        <div className="text-center py-12 text-neutral-500 text-sm rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)' }}>
          No kid profiles found — add kids in the Kids Zone first.
        </div>
      )}

      {kids.map(kid => {
        const plan = plans[kid.kid_id];
        const busy = generating === kid.kid_id || generating === 'all';
        return (
          <div key={kid.kid_id} className="rounded-2xl p-4 space-y-3" data-testid={`dabba-kid-card-${kid.kid_id}`}
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xl" style={{ backgroundColor: kid.avatar_color || '#7c3aed' }}>{kid.avatar_emoji || '🧒'}</div>
                <div>
                  <p className="text-white font-bold text-sm">{kid.name}'s Dabba</p>
                  {plan?.generated_at && <p className="text-[10px] text-neutral-500">AI planned {new Date(plan.generated_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</p>}
                </div>
              </div>
              <button onClick={() => generate(kid.kid_id)} disabled={!!generating} data-testid={`dabba-generate-btn-${kid.kid_id}`}
                className="px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 border bg-purple-500/15 text-purple-200 border-purple-500/35 hover:bg-purple-500/25 disabled:opacity-50">
                <Sparkle size={13} weight="fill" className={busy ? 'animate-spin' : ''} />
                {busy ? 'Planning…' : plan ? 'Regenerate Week' : 'Generate with AI'}
              </button>
            </div>

            <RotationEditor api={api} kid={kid} onSaved={() => load(weekStart)} />

            {plan ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead>
                    <tr>
                      <th className="text-left text-[10px] uppercase tracking-wider font-bold text-neutral-500 px-2 py-1.5 w-24">Day</th>
                      {BOXES.map(b => (
                        <th key={b.id} className="text-left text-[10px] uppercase tracking-wider font-bold text-neutral-500 px-2 py-1.5">{b.emoji} {b.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {DAYS.map((d, i) => {
                      const jf = jainFlags[addDays(weekStart, i)];
                      return (
                      <tr key={d.id} className="border-t border-white/5">
                        <td className="px-2 py-1 text-xs font-semibold text-neutral-400">
                          {d.label}
                          <span className="block text-[9px] text-neutral-600">{fmt(addDays(weekStart, i))}</span>
                          {jf?.no_veg && (
                            <span data-testid={`dabba-jain-${kid.kid_id}-${d.id}`} title="No-vegetable day — tiffin planned accordingly"
                              className={`inline-block mt-0.5 px-1.5 py-px rounded-full text-[8px] font-bold uppercase border ${jf.paryushan ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-amber-500/20 text-amber-300 border-amber-500/40'}`}>
                              🙏 {jf.paryushan ? 'Paryushan' : jf.label}
                            </span>
                          )}
                        </td>
                        {BOXES.map(b => (
                          <td key={b.id} className="px-1 py-1 align-top">
                            <BoxCell plan={plan} day={d.id} box={b} api={api} onUpdate={() => load(weekStart)} />
                          </td>
                        ))}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-neutral-500 italic text-center py-4">No dabba plan for this week yet — hit "Generate with AI" 🍱</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
